import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/Textarea";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  Filter,
  DollarSign,
  Package,
  Tag,
  AlertCircle,
  CheckCircle,
  X
} from "lucide-react";

export default function EquipmentMaterialMaster() {
  const [currentView, setCurrentView] = useState("list"); // list, add, edit
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");

  const [items, setItems] = useState([
    {
      id: "1",
      officialName: "マイクロバイポーラ鉗子（ストレート）",
      nicknames: ["ヤキ", "赤バイ", "バイポ"],
      category: "電気メス用器械",
      storageLocation: "倉庫A-棚3",
      unitCost: 1250,
      isBorrowed: false,
      manufacturer: "オリンパス",
      modelNumber: "MB-W611",
      description: "微細血管凝固用バイポーラ鉗子",
      usedInProcedures: ["幽門側胃切除", "膵頭十二指腸切除"],
      stockQuantity: 15,
      reorderLevel: 5,
      isActive: true,
      createdAt: "2025-01-15",
      updatedAt: "2025-06-01"
    },
    {
      id: "2",
      officialName: "腹腔鏡用トロッカー（12mm）",
      nicknames: ["トロッカー", "ポート", "穴あけ"],
      category: "腹腔鏡器械",
      storageLocation: "倉庫B-棚1",
      unitCost: 850,
      isBorrowed: false,
      manufacturer: "エチコン",
      modelNumber: "ECPS12",
      description: "12mm径腹腔鏡用使い捨てトロッカー",
      usedInProcedures: ["腹腔鏡下胆嚢摘出術", "腹腔鏡下虫垂切除術"],
      stockQuantity: 25,
      reorderLevel: 10,
      isActive: true,
      createdAt: "2025-02-01",
      updatedAt: "2025-06-03"
    },
    {
      id: "3",
      officialName: "チタンプレート（2.4mm、6穴）",
      nicknames: ["プレート", "チタンプレ", "骨プレート"],
      category: "インプラント",
      storageLocation: "医在庫室",
      unitCost: 45000,
      isBorrowed: true,
      manufacturer: "ストライカー",
      modelNumber: "TI-2406",
      description: "整形外科用チタン製固定プレート",
      usedInProcedures: ["橈骨骨折手術", "上腕骨骨折手術"],
      stockQuantity: 3,
      reorderLevel: 2,
      isActive: true,
      createdAt: "2025-03-10",
      updatedAt: "2025-06-05"
    },
    {
      id: "4",
      officialName: "縫合糸（3-0バイクリル）",
      nicknames: ["3-0", "バイクリル", "縫合"],
      category: "縫合材料",
      storageLocation: "症例カート",
      unitCost: 280,
      isBorrowed: false,
      manufacturer: "ジョンソン・エンド・ジョンソン",
      modelNumber: "J491H",
      description: "吸収性合成縫合糸",
      usedInProcedures: ["幽門側胃切除", "腹腔鏡下胆嚢摘出術"],
      stockQuantity: 120,
      reorderLevel: 30,
      isActive: true,
      createdAt: "2025-01-20",
      updatedAt: "2025-06-02"
    }
  ]);

  const categories = ["all", "電気メス用器械", "腹腔鏡器械", "縫合材料", "インプラント", "開腹器械", "麻酔用品"];
  const storageLocations = ["all", "症例カート", "倉庫A-棚3", "倉庫B-棚1", "医在庫室", "スタンバイ室"];

  const [newItem, setNewItem] = useState({
    officialName: "",
    nicknames: [],
    category: "電気メス用器械",
    storageLocation: "倉庫A-棚3",
    unitCost: 0,
    isBorrowed: false,
    manufacturer: "",
    modelNumber: "",
    description: "",
    stockQuantity: 0,
    reorderLevel: 0
  });

  const [newNickname, setNewNickname] = useState("");

  // Stock status function
  const getStockStatus = (item) => {
    if (item.stockQuantity <= item.reorderLevel) {
      return { status: "low", color: "bg-red-100 text-red-800", icon: AlertCircle };
    } else if (item.stockQuantity <= item.reorderLevel * 2) {
      return { status: "medium", color: "bg-yellow-100 text-yellow-800", icon: AlertCircle };
    } else {
      return { status: "good", color: "bg-green-100 text-green-800", icon: CheckCircle };
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = 
      item.officialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.nicknames.some(nickname => nickname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      item.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.modelNumber.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === "all" || item.category === filterCategory;
    const matchesLocation = filterLocation === "all" || item.storageLocation === filterLocation;
    
    return matchesSearch && matchesCategory && matchesLocation && item.isActive;
  });

  const addNickname = (itemData, setItemData) => {
    if (newNickname.trim() && !itemData.nicknames.includes(newNickname.trim())) {
      setItemData({
        ...itemData,
        nicknames: [...itemData.nicknames, newNickname.trim()]
      });
      setNewNickname("");
    }
  };

  const removeNickname = (itemData, setItemData, nickname) => {
    setItemData({
      ...itemData,
      nicknames: itemData.nicknames.filter(n => n !== nickname)
    });
  };

  const saveNewItem = () => {
    const itemToSave = {
      ...newItem,
      id: Date.now().toString(),
      usedInProcedures: [],
      isActive: true,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0]
    };
    
    setItems([...items, itemToSave]);
    setNewItem({
      officialName: "",
      nicknames: [],
      category: "電気メス用器械",
      storageLocation: "倉庫A-棚3",
      unitCost: 0,
      isBorrowed: false,
      manufacturer: "",
      modelNumber: "",
      description: "",
      stockQuantity: 0,
      reorderLevel: 0
    });
    setCurrentView("list");
  };

  const renderItemList = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">器械・材料マスタ</h1>
        <Button onClick={() => setCurrentView("add")}>
          <Plus className="w-4 h-4 mr-2" />
          新規登録
        </Button>
      </div>

      {/* 検索・フィルタ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="器械名・通称・メーカー・型番で検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === "all" ? "全カテゴリ" : cat}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            {storageLocations.map(loc => (
              <option key={loc} value={loc}>
                {loc === "all" ? "全保管場所" : loc}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* アイテム一覧 */}
      <div className="space-y-4">
        {filteredItems.map(item => {
          const stockStatus = getStockStatus(item);
          const StockIcon = stockStatus.icon;
          
          return (
            <Card key={item.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold">{item.officialName}</h3>
                      {item.isBorrowed && (
                        <Badge className="bg-red-100 text-red-800">借用品</Badge>
                      )}
                      <Badge variant="outline">{item.category}</Badge>
                    </div>

                    {/* 通称表示 */}
                    {item.nicknames.length > 0 && (
                      <div className="mb-2">
                        <span className="text-sm text-gray-600">通称: </span>
                        {item.nicknames.map((nickname, index) => (
                          <Badge key={index} variant="secondary" className="mr-1 text-xs">
                            {nickname}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">メーカー:</span> {item.manufacturer}
                      </div>
                      <div>
                        <span className="font-medium">型番:</span> {item.modelNumber}
                      </div>
                      <div>
                        <span className="font-medium">保管場所:</span> {item.storageLocation}
                      </div>
                      <div>
                        <span className="font-medium">単価:</span> ¥{item.unitCost.toLocaleString()}
                      </div>
                    </div>

                    <div className="mt-2 text-sm text-gray-600">
                      {item.description}
                    </div>

                    {/* 在庫状況 */}
                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <StockIcon className="w-4 h-4" />
                        <span className={`text-xs px-2 py-1 rounded ${stockStatus.color}`}>
                          在庫: {item.stockQuantity}個 
                          {stockStatus.status === 'low' && ' (要発注)'}
                        </span>
                      </div>
                      
                      {item.usedInProcedures.length > 0 && (
                        <div className="text-xs text-gray-500">
                          使用術式: {item.usedInProcedures.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedItem(item);
                        setCurrentView("edit");
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setItems(items.map(i => 
                          i.id === item.id ? { ...i, isActive: false } : i
                        ));
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderItemForm = (isEdit = false) => {
    const itemData = isEdit ? selectedItem : newItem;
    const setItemData = isEdit ? setSelectedItem : setNewItem;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            {isEdit ? "器械・材料編集" : "器械・材料新規登録"}
          </h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentView("list")}
            >
              キャンセル
            </Button>
            <Button
              onClick={isEdit ? () => {
                setItems(items.map(i => 
                  i.id === selectedItem.id ? { ...selectedItem, updatedAt: new Date().toISOString().split('T')[0] } : i
                ));
                setCurrentView("list");
              } : saveNewItem}
            >
              <Save className="w-4 h-4 mr-2" />
              保存
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">基本情報</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">器械・材料名（正式名称）</label>
                <Input
                  value={itemData.officialName}
                  onChange={(e) => setItemData({
                    ...itemData,
                    officialName: e.target.value
                  })}
                  placeholder="例: マイクロバイポーラ鉗子（ストレート）"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">カテゴリ</label>
                  <select
                    value={itemData.category}
                    onChange={(e) => setItemData({
                      ...itemData,
                      category: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {categories.slice(1).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">メーカー</label>
                  <Input
                    value={itemData.manufacturer}
                    onChange={(e) => setItemData({
                      ...itemData,
                      manufacturer: e.target.value
                    })}
                    placeholder="例: オリンパス"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">型番</label>
                  <Input
                    value={itemData.modelNumber}
                    onChange={(e) => setItemData({
                      ...itemData,
                      modelNumber: e.target.value
                    })}
                    placeholder="例: MB-W611"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">単価（円）</label>
                  <Input
                    type="number"
                    value={itemData.unitCost}
                    onChange={(e) => setItemData({
                      ...itemData,
                      unitCost: Number(e.target.value)
                    })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">説明</label>
                <Textarea
                  value={itemData.description}
                  onChange={(e) => setItemData({
                    ...itemData,
                    description: e.target.value
                  })}
                  rows={3}
                  placeholder="器械・材料の詳細説明"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">通称・俗称</h2>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newNickname}
                  onChange={(e) => setNewNickname(e.target.value)}
                  placeholder="通称を入力..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addNickname(itemData, setItemData);
                    }
                  }}
                />
                <Button 
                  variant="outline" 
                  onClick={() => addNickname(itemData, setItemData)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {itemData.nicknames.map((nickname, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {nickname}
                    <button
                      onClick={() => removeNickname(itemData, setItemData, nickname)}
                      className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">在庫・コスト情報</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">保管場所</label>
                  <select
                    value={itemData.storageLocation}
                    onChange={(e) => setItemData({
                      ...itemData,
                      storageLocation: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {storageLocations.slice(1).map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">現在在庫</label>
                  <Input
                    type="number"
                    value={itemData.stockQuantity}
                    onChange={(e) => setItemData({
                      ...itemData,
                      stockQuantity: Number(e.target.value)
                    })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">発注点</label>
                  <Input
                    type="number"
                    value={itemData.reorderLevel}
                    onChange={(e) => setItemData({
                      ...itemData,
                      reorderLevel: Number(e.target.value)
                    })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isBorrowed"
                  checked={itemData.isBorrowed}
                  onChange={(e) => setItemData({
                    ...itemData,
                    isBorrowed: e.target.checked
                  })}
                  className="rounded"
                />
                <label htmlFor="isBorrowed" className="text-sm font-medium">
                  借用品（医在庫から借用する材料）
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="p-6">
      {currentView === "list" && renderItemList()}
      {currentView === "add" && renderItemForm(false)}
      {currentView === "edit" && renderItemForm(true)}
    </div>
  );
}